import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import { useStore } from '@nanostores/react';
import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { VList, type VListHandle } from 'virtua';

import type { EndpointResponse } from '@iconik-io/iconik-api/src/client/response';
import type {
  AssetsAssetElasticSchemaWithIdNullable,
  AssetsCollectionElasticSchemaWithIdNullable,
} from '@iconik-io/iconik-api/src/services/assets';
import {
  httpPostSearch,
  type SearchSearchCriteriaSchema,
} from '@iconik-io/iconik-api/src/services/search';
import { useVirtualizedScrollToIds } from '@iconik-io/shared-lib/src/react/hooks/use_virtualized_scroll_to_ids';
import { getProxyTimecodeOptions } from '@iconik-io/shared-lib/src/utils/get_proxy_timecode_options';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { AssetSegmentForm } from '../../components/asset_segment_form/asset_segment_form';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import { SEGMENT_FORM_TYPE_SUBCLIP } from '../../components/segment_form/constants';
import { SUBCLIPS } from '../../constants/help_urls';
import { getBaseUrl } from '../../ducks/app/selectors';
import {
  selectAssetProxy,
  selectSubclipAssets,
} from '../../ducks/pages/asset/selectors';
import { highlightSubclip } from '../../ducks/panels/subclips/thunks';
import { selectCanCreateSubclips } from '../../ducks/roles/selectors';
import { selectCurrentVersionId } from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import SubclipItem from './subclip_item';

import assetCommentsStyles from '../asset_comments/asset_comments.module.css';
import styles from './asset_subclips.module.css';

function getSearchCriteriaForSubclips(
  assetId: string,
  versionId?: string,
): SearchSearchCriteriaSchema {
  const terms = [
    {
      name: 'original_asset_id',
      value: assetId,
    },
    {
      name: 'type',
      value: 'SUBCLIP',
    },
    {
      name: 'status',
      value: 'ACTIVE',
    },
  ];

  if (versionId) {
    terms.push({
      name: 'original_version_id',
      value: versionId,
    });
  }

  return {
    doc_types: ['assets'],
    include_fields: [
      'id',
      'title',
      'parent_id',
      'created_by_user',
      'date_created',
      'date_modified',
      'original_version_id',
      'original_asset_id',
      'original_segment_id',
      'time_start_milliseconds',
      'time_end_milliseconds',
      'metadata',
      'type',
      'object_type',
      'keyframes',
    ],
    sort: [{ name: 'date_created', order: 'desc' }],
    filter: {
      operator: 'AND',
      terms,
    },
  };
}

export const AssetSubclipsPanel: FC = () => {
  const dispatch = useAppDispatch();

  const { store } = useBasePlayerContext();
  const [isFormOpened, setIsFormOpened] = useState(false);
  const { id: assetId, versionId } = useParams({ strict: false });
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, versionId),
  );
  const query = useAppSelector((state) => state.routeParams.query);
  const proxy = useAppSelector((state) =>
    selectAssetProxy(state, assetId, versionId),
  );
  const baseUrl = useAppSelector((state) => getBaseUrl(state));
  const [isLoading, setIsLoading] = useState(true);
  const subclipAssets = useAppSelector((state) =>
    selectSubclipAssets(state, assetId, versionId),
  );
  const highlightedSubclipIds = useAppSelector(
    (state) => state.panelsState.subclips.highlightedSubclipIds,
  );
  const vListRef = useRef<VListHandle>(null);
  const { setScrollToIds } = useVirtualizedScrollToIds({
    virtualizer: vListRef.current,
    ids: subclipAssets.map(({ id }) => id),
  });

  const selectedSegmentIds = useStore(store.segment.$selectedSegmentIds);
  const timecodeOptions = proxy ? getProxyTimecodeOptions(proxy) : undefined;
  const canCreateSubclips = useAppSelector((state) =>
    selectCanCreateSubclips(state, assetId, currentVersionId),
  );
  useEffect(() => {
    if (!highlightedSubclipIds || highlightedSubclipIds.length === 0) {
      return;
    }
    setScrollToIds(highlightedSubclipIds);
  }, [highlightedSubclipIds]);

  useEffect(() => {
    if (!assetId) {
      return;
    }
    setIsLoading(true);
    const requestParams = getSearchCriteriaForSubclips(
      assetId,
      currentVersionId,
    );
    let subclips: (
      | AssetsAssetElasticSchemaWithIdNullable
      | AssetsCollectionElasticSchemaWithIdNullable
    )[] = [];

    const resolve = (
      { body }: EndpointResponse<ReturnType<typeof httpPostSearch>>,
      page: number | undefined,
    ): Promise<
      (
        | AssetsAssetElasticSchemaWithIdNullable
        | AssetsCollectionElasticSchemaWithIdNullable
      )[]
    > => {
      subclips = subclips.concat(body.objects);

      if (page && body.pages > page) {
        return dispatch(httpPostSearch(requestParams, { page: page + 1 })).then(
          (response) => resolve(response, page + 1),
        );
      }

      return Promise.resolve(subclips);
    };
    dispatch(httpPostSearch(requestParams))
      .then((response) => resolve(response, response.body.page))
      .then(() => {
        if (query.subclip_id) {
          dispatch(highlightSubclip([query.subclip_id]));
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [assetId, currentVersionId, query.subclip_id]);

  const handleSubclipCreated = useCallback(() => {
    setIsFormOpened(false);
  }, []);

  useEffect(() => {
    if (selectedSegmentIds.length > 0) {
      dispatch(highlightSubclip(selectedSegmentIds));
    }
  }, [selectedSegmentIds]);

  const subclipElements = subclipAssets.map((subclipAsset) => {
    const className = classNames(styles.subclip_item, {
      [styles.highlight]: highlightedSubclipIds?.includes(subclipAsset.id),
    });
    return (
      <div className={className} key={subclipAsset.id} role="listitem">
        <SubclipItem
          subclip={subclipAsset}
          timecodeOptions={timecodeOptions}
          baseUrl={baseUrl}
        />
      </div>
    );
  });

  const handleCancel = useCallback(() => setIsFormOpened(false), []);

  return (
    <>
      <div
        className={classNames(styles.root, {
          [assetCommentsStyles.list_wrapper_disabled]: isFormOpened,
        })}
      >
        {isLoading && <LoadingBar />}
        {!isLoading && subclipAssets.length > 0 ? (
          <VList ref={vListRef} role="list">
            {subclipElements}
          </VList>
        ) : null}
        {!isLoading && subclipAssets.length === 0 ? (
          <NoContentPanel
            title="Save time-based segments of your assets for easy reference and more granular metadata tagging"
            linkText="Learn more"
            linkHref={SUBCLIPS}
            actionText="CREATE SUBCLIP"
            onActionClick={
              canCreateSubclips ? () => setIsFormOpened(true) : undefined
            }
          />
        ) : null}
      </div>
      {canCreateSubclips ? (
        <div className={assetCommentsStyles.segment_form_wrapper}>
          {isFormOpened ? (
            <AssetSegmentForm
              formClassName={assetCommentsStyles.segment_form}
              formType={SEGMENT_FORM_TYPE_SUBCLIP}
              onSegmentCreated={handleSubclipCreated}
              onCancel={handleCancel}
            />
          ) : (
            <Button
              className={assetCommentsStyles.open_segment_form_button}
              onClick={() => setIsFormOpened(true)}
              wideBasis
            >
              Create subclip
            </Button>
          )}
        </div>
      ) : null}
    </>
  );
};
